import React from 'react';
import { Table, Tag, Button, Space, Card, Tooltip, Typography, Empty, Badge, Grid } from 'antd';
import {
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    SendOutlined,
    QuestionCircleOutlined,
    InboxOutlined,
    CloudSyncOutlined,
    CheckSquareOutlined,
    BulbOutlined,
    CheckOutlined,
    UserOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

export default function MovimentacaoFinanceiraTable({
    data,
    loading,
    onEdit,
    onDelete,
    onView,
    selectedRowKeys,
    setSelectedRowKeys,
    onBulkDelete,
    onDeleteAll,
    onDeleteDuplicates,
    isContador,
    onRequestConfirmation,
    onMarkAsUnidentified,
    bankAccounts = []
}) {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    const getSituacaoId = (situacao) => {
        if (!situacao) return 'A';
        return typeof situacao === 'object' ? situacao.id : situacao;
    };

    // Motor de Sugestão IA Simulado
    const getAiSuggestion = (record) => {
        if (record.categoria?.id) return null;
        
        const desc = (record.descricao || '').toUpperCase();
        
        if (desc.includes('POSTO') || desc.includes('SHELL') || desc.includes('IPIRANGA')) return 'Combustível';
        if (desc.includes('ALUGUEL') || desc.includes('CONDOMINIO')) return 'Aluguel e Encargos';
        if (desc.includes('ENEL') || desc.includes('ENERGIA') || desc.includes('LUZ')) return 'Energia Elétrica';
        if (desc.includes('AGUA') || desc.includes('SABESP')) return 'Água e Esgoto';
        if (desc.includes('INTERNET') || desc.includes('VIVO') || desc.includes('CLARO')) return 'Telefone e Internet';
        if (desc.includes('TAR') || desc.includes('IOF') || desc.includes('BANCO')) return 'Tarifas Bancárias';
        if (desc.includes('FGTS') || desc.includes('INSS') || desc.includes('SALARIO')) return 'Folha de Pagamento';
        
        return null;
    };

    const columns = [
        {
            title: 'Data',
            dataIndex: 'dataReferencia',
            key: 'dataReferencia',
            width: 110,
            render: (date) => (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Text strong style={{ color: '#1e293b', fontSize: '13px' }}>
                        {dayjs(date).format('DD/MM/YYYY')}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                        {dayjs(date).format('ddd')}
                    </Text>
                </div>
            )
        },
        ...(isContador ? [{
            title: 'Situação',
            dataIndex: 'situacao',
            key: 'situacao',
            width: 150,
            render: (situacao) => {
                const sId = getSituacaoId(situacao);
                let config = { color: '#64748b', bg: '#f1f5f9', text: 'Aberto' };

                if (sId === 'L') config = { color: '#059669', bg: '#ecfdf5', text: 'Liquidado' };
                else if (sId === 'C') config = { color: '#e11d48', bg: '#fff1f2', text: 'Cancelado' };
                else if (sId === 'N') config = { color: '#7c3aed', bg: '#f5f3ff', text: 'Não Identificado' };
                else if (sId === 'W') config = { color: '#d97706', bg: '#fffbeb', text: 'Aguardando' };

                return (
                    <div style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        padding: '2px 10px', 
                        borderRadius: '20px', 
                        backgroundColor: config.bg, 
                        color: config.color,
                        fontSize: '11px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.02em',
                        border: `1px solid ${config.color}20`
                    }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: config.color, marginRight: 8 }} />
                        {config.text}
                    </div>
                );
            }
        }] : []),
        {
            title: 'Categoria',
            dataIndex: 'categoria',
            key: 'categoria',
            width: 200,
            render: (categoria, record) => {
                const suggestion = getAiSuggestion(record);
                
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                        {categoria?.nome ? (
                            <div style={{ 
                                color: '#475569', 
                                fontSize: '12px', 
                                fontWeight: 600,
                                background: '#f1f5f9',
                                padding: '2px 8px',
                                borderRadius: '6px',
                                border: '1px solid #e2e8f0',
                                display: 'inline-block'
                            }}>
                                {categoria.nome}
                            </div>
                        ) : suggestion ? (
                            <Tooltip title="Clique para confirmar esta sugestão inteligente">
                                <div 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onEdit(record); // Abre o modal com a sugestão (futuramente podemos fazer confirmação direta)
                                    }}
                                    style={{ 
                                        color: '#3b82f6', 
                                        fontSize: '11px', 
                                        fontWeight: 700,
                                        background: '#eff6ff',
                                        padding: '2px 8px',
                                        borderRadius: '6px',
                                        border: '1px dashed #3b82f6',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4
                                    }}
                                >
                                    <BulbOutlined style={{ fontSize: 12 }} />
                                    SUGESTÃO IA: {suggestion.toUpperCase()}
                                </div>
                            </Tooltip>
                        ) : (
                            <Text type="secondary" style={{ fontSize: '11px', fontStyle: 'italic' }}>Não Classificado</Text>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Descrição / Histórico',
            dataIndex: 'descricao',
            key: 'descricao',
            width: 300,
            render: (desc, record) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {record.idPluggy && (
                            <Tooltip title="Sincronizado via Open Finance">
                                <CloudSyncOutlined style={{ color: '#3b82f6', fontSize: 14 }} />
                            </Tooltip>
                        )}
                        <Text strong style={{ color: '#334155', fontSize: '13px' }}>
                            {desc || 'Sem descrição'}
                        </Text>
                    </div>
                    {record.contaBancaria && (() => {
                        // Origem do lançamento como TAG (mesmo padrão dos outros selos
                        // da linha): verde "CAIXA" pra dinheiro em espécie, azul com o
                        // nome do banco pra conta bancária.
                        //
                        // CUIDADO: NÃO dá pra decidir Caixa pelo id === 0. Os lançamentos
                        // vindos do Pantheon (2026+) chegam com contaBancaria.id = 0
                        // (o DTO Java tem id primitivo int e o caminho do Pantheon nunca
                        // seta id), mas TRAZEM banco/agência/conta. O discriminador certo
                        // é a PRESENÇA de dados bancários — se tem banco, é banco.
                        const cb = record.contaBancaria;
                        const cbObj = typeof cb === 'object' && cb !== null;
                        const cbId = cbObj ? cb?.id : cb;
                        const nomeBanco = cbObj ? (cb.banco?.nome || cb.nome) : null;
                        const numConta = cbObj ? cb.conta : null;
                        const temDadosBanco = !!(nomeBanco || numConta || (cbObj && cb.agencia));

                        // Caixa só quando NÃO há nenhum dado bancário. Id 0/ausente
                        // sozinho não basta (é o default do Pantheon).
                        if (!temDadosBanco) {
                            return (
                                <Tag color="green" style={{ fontSize: '10px', lineHeight: '16px', marginInlineEnd: 0 }}>
                                    CAIXA
                                </Tag>
                            );
                        }
                        // Se casar com uma conta cadastrada (id real > 0), usa o rótulo
                        // rico dela; senão mostra o que veio no próprio lançamento.
                        const account = (cbId && String(cbId) !== '0')
                            ? bankAccounts.find(acc => String(acc.id) === String(cbId))
                            : null;
                        const rotulo = account
                            ? `${account.banco?.nome || 'Banco'}${account.conta ? ' - ' + account.conta : ''}`
                            : `${nomeBanco || 'Banco'}${numConta ? ' - ' + numConta : ''}`;
                        return (
                            <Tag color="blue" style={{ fontSize: '10px', lineHeight: '16px', marginInlineEnd: 0, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {rotulo}
                            </Tag>
                        );
                    })()}
                </div>
            ),
        },
        ...(isContador ? [{
            title: 'Usuário',
            key: 'usuario',
            width: 150,
            render: (_, record) => {
                const userCad = record.usuarioCadastro?.nome || record.usuarioCadastro?.id || (typeof record.usuarioCadastro === 'string' ? record.usuarioCadastro : null);
                const userAlt = record.usuarioAlteracao?.nome || record.usuarioAlteracao?.id || (typeof record.usuarioAlteracao === 'string' ? record.usuarioAlteracao : null);
                
                return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Tooltip title={`Cadastrado em: ${record.dataCadastro ? dayjs(record.dataCadastro).format('DD/MM/YYYY HH:mm') : 'N/A'}`}>
                            <Space size={4}>
                                <UserOutlined style={{ fontSize: '12px', color: '#64748b' }} />
                                <Text style={{ fontSize: '12px', color: '#475569', fontWeight: 500 }}>{userCad || 'Sistema'}</Text>
                            </Space>
                        </Tooltip>
                        {userAlt && (
                            <Tooltip title={`Alterado em: ${record.dataAlteracao ? dayjs(record.dataAlteracao).format('DD/MM/YYYY HH:mm') : 'N/A'}`}>
                                <Space size={4}>
                                    <EditOutlined style={{ fontSize: '10px', color: '#faad14' }} />
                                    <Text type="secondary" style={{ fontSize: '11px' }}>{userAlt}</Text>
                                </Space>
                            </Tooltip>
                        )}
                    </div>
                );
            }
        }] : []),
        {
            title: 'Valor',
            dataIndex: 'valor',
            key: 'valor',
            align: 'right',
            width: 140,
            render: (valor, record) => {
                const tId = typeof record.tipo === 'object' ? record.tipo?.id : record.tipo;
                const isCredit = tId === 'C';
                const sId = getSituacaoId(record.situacao);
                const isLiquidado = sId === 'L';
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {isContador && isLiquidado && (
                                <Tooltip title="Lançamento Validado/Conciliado">
                                    <CheckCircleOutlined style={{ color: '#059669', fontSize: 12 }} />
                                </Tooltip>
                            )}
                            <Text strong style={{ 
                                color: isCredit ? '#059669' : '#e11d48',
                                fontSize: '14px'
                            }}>
                                {isCredit ? '+' : '-'} {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor)}
                            </Text>
                        </div>
                        <Text type="secondary" style={{ fontSize: '10px', textTransform: 'uppercase', fontWeight: 600 }}>
                            {isCredit ? 'Receita' : 'Despesa'}
                        </Text>
                    </div>
                );
            }
        },
        {
            title: '',
            key: 'actions',
            align: 'right',
            width: 120,
            render: (_, record) => (
                <Space size={0}>
                    <Tooltip title="Ver detalhes">
                        <Button
                            type="text"
                            size="small"
                            icon={<EyeOutlined style={{ color: '#94a3b8' }} />}
                            onClick={() => onView(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Editar">
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined style={{ color: '#94a3b8' }} />}
                            onClick={() => onEdit(record)}
                        />
                    </Tooltip>
                    {isContador && (
                        <Tooltip title="Solicitar Comprovante ao Cliente">
                            <Button
                                type="text"
                                size="small"
                                icon={<SendOutlined style={{ color: '#3b82f6' }} />}
                                onClick={() => onRequestConfirmation(record)}
                            />
                        </Tooltip>
                    )}
                    <Tooltip title="Excluir">
                        <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined style={{ opacity: 0.6 }} />}
                            onClick={() => onDelete(record)}
                        />
                    </Tooltip>
                </Space>
            )
        }
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
    };

    return (
        <Card bordered={false} className="premium-card" style={{ borderRadius: 12 }} bodyStyle={{ padding: isMobile ? '12px' : '24px' }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: isMobile ? 'column' : 'row', gap: 12 }}>
                <Title level={5} style={{ margin: 0 }}>
                    Extrato de Movimentações
                </Title>
                <Space size={12}>
                    {data.length > 0 && isContador && (
                        <Space size={8}>
                            <Button
                                type="text"
                                icon={<InboxOutlined />}
                                onClick={onDeleteDuplicates}
                                style={{ borderRadius: '8px', fontWeight: 600, color: '#6366f1' }}
                            >
                                Limpar Duplicados
                            </Button>
                            <Button
                                danger
                                type="text"
                                icon={<DeleteOutlined />}
                                onClick={onDeleteAll}
                                style={{ borderRadius: '8px', fontWeight: 'bold' }}
                            >
                                Excluir Tudo
                            </Button>
                        </Space>
                    )}
                </Space>
            </div>
            <Table
                rowSelection={isContador ? rowSelection : null}
                columns={columns}
                dataSource={data}
                rowKey="id"
                loading={loading}
                scroll={{ x: 'max-content' }}
                size={isMobile ? 'small' : 'middle'}
                sticky
                locale={{
                    emptyText: (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <Space direction="vertical" size={0}>
                                    <Text type="secondary" strong>Nenhum lançamento encontrado</Text>
                                    <Text type="secondary">Tente ajustar o período ou os filtros de busca.</Text>
                                </Space>
                            }
                        />
                    )
                }}
                pagination={{
                    defaultPageSize: 20,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100', '250'],
                    showTotal: (total) => `Total de ${total} lançamentos`,
                    position: ['bottomRight']
                }}
                className="premium-table"
            />
        </Card>
    );
}
